import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { StrategyGuidanceBanner } from '../strategy/StrategyGuidanceBanner';
import { NumericInput } from '../strategy/NumericInput';
import { SciencesOverviewTab } from './SciencesOverviewTab';
import { formatNumber } from '../../utils/format';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import type { SciencesShellProps, SciencesTabKey } from '../../pages/Science';
import type { ScienceType } from '../../types/science';

const TABS: readonly { key: SciencesTabKey; label: string }[] = [
  { key: 'invest', label: 'Invest' },
  { key: 'overview', label: 'Overview' },
];

/**
 * SF-140: thin wrapper around the existing (already desktop-appropriate) Sciences JSX — no
 * layout redesign. Adds a real, focus-managed `<h1>`; desktop's existing inline
 * submitError/successMessage rendering is preserved as-is (matching SF-132/139's precedent —
 * only mobile moves to useToast()).
 *
 * SF-161: adds a two-tab, bottom-border strip (Invest/Overview) with full WAI-ARIA — a deliberate
 * improvement over DesktopTrainingShell's tab strip, which has none (Phase 3 Decision 1).
 */
export function DesktopSciencesShell({
  province,
  activeStrategy,
  strategyLoading,
  activeTab,
  setActiveTab,
  researchRate,
  booksToInvest,
  isSubmittingSettings,
  isSubmittingInvestment,
  submitError,
  successMessage,
  handleResearchRateChange,
  handleSubmitSettings,
  handleBooksToInvestChange,
  handleSubmitInvestment,
  handleFillStrategy,
  researchRateOptions,
  scienceTypes,
  unallocatedBooks,
  investmentProgress,
  investments,
  calculatedValues,
  totalBooksToInvest,
  investmentValid,
}: SciencesShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const selectStyle = { ...INPUT_STYLE, border: `1px solid ${COLORS.inputBorder}` } as const;
  const canFillStrategy = Boolean(activeStrategy?.settings?.scienceAlloc) && unallocatedBooks > 0;

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Sciences
      </h1>

      <StrategyGuidanceBanner activeStrategy={activeStrategy} isLoading={strategyLoading} page="science" />

      <div className="flex border-b" style={{ borderColor: COLORS.border }} role="tablist">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              id={`sciences-tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`sciences-panel-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div role="tabpanel" id="sciences-panel-overview" aria-labelledby="sciences-tab-overview">
          <SciencesOverviewTab province={province} investments={investments} investmentProgress={investmentProgress} />
        </div>
      )}

      {activeTab === 'invest' && (
      <div role="tabpanel" id="sciences-panel-invest" aria-labelledby="sciences-tab-invest" className="space-y-4">
      {/* Stat Cards */}
      <div className="flex gap-3">
        <div className="w-36">
          <StatCard
            label="Books Available"
            value={`${formatNumber(unallocatedBooks)}`}
            sub="Unallocated books"
            color={unallocatedBooks > 0 ? COLORS.positive : undefined}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Books / Day"
            value={`${formatNumber(calculatedValues.booksPerTick)}`}
            sub="Research output"
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Research Cost"
            value={`${formatNumber(calculatedValues.goldCost)} gc`}
            sub={`${calculatedValues.costPercentage.toFixed(1)}% of income`}
            color={COLORS.gold}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Daily Income"
            value={`${formatNumber(province.economy.lastTickChanges.goldIncome)} gc`}
            sub="Gold income"
            borderless
          />
        </div>
      </div>

      {/* Research Rate Selection */}
      <div
        className="rounded p-4"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center gap-4">
          <label htmlFor="research-rate-select" className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>
            Research Rate
          </label>
          <select
            id="research-rate-select"
            value={researchRate}
            onChange={(e) => handleResearchRateChange(e.target.value)}
            className="flex-1 rounded text-sm py-1.5 px-2"
            style={selectStyle}
          >
            {researchRateOptions.map((rate) => {
              const books = Math.floor(province.territory.totals.acres * rate.bpa);
              return (
                <option key={rate.value} value={rate.value}>
                  {rate.label} ({rate.bpa} BPA, {rate.gcPerBook}gc/book → {books} books)
                </option>
              );
            })}
          </select>
          <Button
            onClick={handleSubmitSettings}
            isLoading={isSubmittingSettings}
          >
            Update Rate
          </Button>
        </div>
      </div>

      {/* Book Investment Table + Submit */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        {/* Header */}
        <div
          className="grid items-center px-3 py-1.5"
          style={{
            gridTemplateColumns: '100px 1fr 80px 80px 80px',
            backgroundColor: COLORS.sectionHeader,
          }}
        >
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>
            Branch
          </span>
          <span className="text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>EFFECT</span>
          <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>KNOWN</span>
          <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>IN PROG</span>
          <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>INVEST</span>
        </div>

        {scienceTypes.map((scienceType) => {
          const books = province.sciences?.books?.[scienceType.type] ?? 0;
          const inProgress = investmentProgress[scienceType.type] ?? 0;

          return (
            <div
              key={scienceType.type}
              className="grid items-center px-3 py-1.5"
              style={{
                gridTemplateColumns: '100px 1fr 80px 80px 80px',
                borderBottom: `1px solid ${COLORS.border}`,
              }}
            >
              <span className="text-sm" style={{ color: COLORS.text.primary }}>{scienceType.name}</span>
              <span className="text-sm" style={{ color: COLORS.text.tertiary }}>
                {scienceType.effectDescription}
              </span>
              <span className="text-center font-mono text-sm" style={{ color: COLORS.text.secondary }}>
                {formatNumber(Math.floor(books))}
              </span>
              <span className="text-center font-mono text-sm" style={{ color: inProgress > 0 ? COLORS.text.secondary : COLORS.text.tertiary }}>
                {inProgress > 0 ? formatNumber(Math.floor(inProgress)) : '—'}
              </span>
              <div className="flex justify-end">
                <NumericInput
                  value={booksToInvest[scienceType.type as ScienceType]}
                  max={unallocatedBooks}
                  onChange={(v) => handleBooksToInvestChange(scienceType.type, v)}
                  ariaLabel={`Books to invest in ${scienceType.name}`}
                  className="w-16"
                />
              </div>
            </div>
          );
        })}

        {/* Submit Bar */}
        <div
          className="px-4 py-3 space-y-3"
          style={{ borderTop: `1px solid ${COLORS.border}` }}
        >
        {/* Messages */}
        {submitError && (
          <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
        )}
        {successMessage && (
          <div className="text-sm" style={{ color: COLORS.positive }}>{successMessage}</div>
        )}

        {/* Summary row */}
        <div className="flex items-center gap-6 text-sm">
          <span className="whitespace-nowrap">
            <span style={{ color: COLORS.text.tertiary }}>Investing: </span>
            <span className="font-mono" style={{ color: totalBooksToInvest > unallocatedBooks ? COLORS.negative : COLORS.text.primary }}>
              {formatNumber(totalBooksToInvest)} books
            </span>
          </span>
          <span className="whitespace-nowrap">
            <span style={{ color: COLORS.text.tertiary }}>Available: </span>
            <span className="font-mono" style={{ color: COLORS.text.primary }}>
              {formatNumber(unallocatedBooks)} books
            </span>
          </span>
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleFillStrategy}
            disabled={!canFillStrategy}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded"
            style={{
              backgroundColor: canFillStrategy ? COLORS.sectionHeader : 'transparent',
              border: `1px solid ${COLORS.border}`,
              color: canFillStrategy ? '#B89878' : COLORS.text.tertiary,
              cursor: canFillStrategy ? 'pointer' : 'default',
            }}
          >
            ⚡ Fill Strategy
          </button>

          <div className="flex gap-2 ml-auto">
            <Button
              onClick={handleSubmitInvestment}
              isLoading={isSubmittingInvestment}
              disabled={!investmentValid}
            >
              Invest Books
            </Button>
          </div>
        </div>
        </div>
      </div>
      </div>
      )}
    </div>
  );
}
